import React, { useContext, useMemo, useState } from 'react';
import { FiDownload, FiFileText, FiPrinter, FiSearch, FiSliders } from 'react-icons/fi';
import { AppContext } from '../Context';
import NavbarGlobal from './NavbarGlobal';
import './MaterialBiblioteca.scss';

const obtenerNivel = (material) => {
    const parsedLevel = Number.parseInt(material.level?.toString().replace('Nivel ', ''), 10);
    return Number.isFinite(parsedLevel) ? parsedLevel : null;
};

function MaterialDidactico() {
    const { materials, currentUser, level, teacher } = useContext(AppContext);
    const [busqueda, setBusqueda] = useState('');
    const [nivelSeleccionado, setNivelSeleccionado] = useState('todos');

    const tieneAcceso = Boolean(level || teacher || currentUser?.isMaster);
    const tieneAccesoTotal = teacher || currentUser?.isMaster;

    const materialesPermitidos = useMemo(() => {
        if (tieneAccesoTotal) return materials;
        if (!level) return [];

        const maxLevel = Number.parseInt(level, 10);
        return materials.filter((material) => {
            const materialLevel = obtenerNivel(material);
            return materialLevel !== null && materialLevel <= maxLevel;
        });
    }, [materials, level, tieneAccesoTotal]);

    const nivelesDisponibles = useMemo(() => (
        [...new Set(materialesPermitidos.map(obtenerNivel).filter(Boolean))].sort((a, b) => a - b)
    ), [materialesPermitidos]);

    const materialesVisibles = useMemo(() => {
        const termino = busqueda.trim().toLocaleLowerCase('es');

        return materialesPermitidos
            .filter((material) => nivelSeleccionado === 'todos'
                || obtenerNivel(material) === Number(nivelSeleccionado))
            .filter((material) => !termino
                || material.name?.toLocaleLowerCase('es').includes(termino))
            .sort((a, b) => {
                const diferenciaNivel = (obtenerNivel(a) || 0) - (obtenerNivel(b) || 0);
                return diferenciaNivel || (a.name || '').localeCompare(b.name || '', 'es');
            });
    }, [busqueda, materialesPermitidos, nivelSeleccionado]);

    return (
        <div className="material-library">
            <NavbarGlobal title="Material para imprimir" showBackButton={true} />
            <main className="material-library__container">
                <section className="material-library__hero material-library__hero--print" aria-labelledby="material-imprimir-title">
                    <div className="material-library__hero-copy">
                        <span className="material-library__eyebrow">
                            <FiPrinter aria-hidden="true" /> Recursos descargables
                        </span>
                        <h1 id="material-imprimir-title">Del aula a tus manos</h1>
                        <p>Encuentra guías y actividades listas para descargar, imprimir y trabajar sin conexión.</p>
                    </div>
                    <div className="material-library__hero-mark" aria-hidden="true">
                        <FiFileText />
                        <strong>{materialesPermitidos.length}</strong>
                        <span>{materialesPermitidos.length === 1 ? 'recurso disponible' : 'recursos disponibles'}</span>
                    </div>
                </section>

                {tieneAcceso ? (
                    <section className="material-library__section" aria-labelledby="recursos-title">
                        <div className="material-library__section-heading">
                            <div>
                                <span className="material-library__section-kicker">Biblioteca imprimible</span>
                                <h2 id="recursos-title">Materiales disponibles</h2>
                            </div>
                        </div>

                        <div className="material-library__toolbar">
                            <label className="material-library__search">
                                <FiSearch aria-hidden="true" />
                                <span className="visually-hidden">Buscar material</span>
                                <input
                                    type="search"
                                    value={busqueda}
                                    onChange={(event) => setBusqueda(event.target.value)}
                                    placeholder="Buscar por nombre…"
                                />
                            </label>
                            <label className="material-library__filter">
                                <FiSliders aria-hidden="true" />
                                <span className="visually-hidden">Filtrar por nivel</span>
                                <select
                                    value={nivelSeleccionado}
                                    onChange={(event) => setNivelSeleccionado(event.target.value)}
                                >
                                    <option value="todos">Todos los niveles</option>
                                    {nivelesDisponibles.map((nivelDisponible) => (
                                        <option key={nivelDisponible} value={nivelDisponible}>Nivel {nivelDisponible}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        {materialesVisibles.length > 0 ? (
                            <div className="print-material-grid">
                                {materialesVisibles.map((material, index) => {
                                    const materialLevel = obtenerNivel(material);
                                    return (
                                        <article className="print-material-card" key={material.id || `${material.name}-${index}`}>
                                            <div className="print-material-card__top">
                                                <span className="print-material-card__file-icon"><FiFileText aria-hidden="true" /></span>
                                                <span className="print-material-card__level">
                                                    {materialLevel ? `Nivel ${materialLevel}` : 'Material general'}
                                                </span>
                                            </div>
                                            <div className="print-material-card__content">
                                                <h3>{material.name || 'Material sin nombre'}</h3>
                                                <p>Recurso listo para consultar, descargar e imprimir.</p>
                                            </div>
                                            <a
                                                className="print-material-card__download"
                                                href={material.url}
                                                download
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label={`Descargar ${material.name || 'material'}`}
                                            >
                                                <FiDownload aria-hidden="true" /> Descargar material
                                            </a>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="material-library__empty material-library__empty--compact" role="status">
                                <span className="material-library__empty-icon"><FiSearch aria-hidden="true" /></span>
                                <h3>No encontramos materiales</h3>
                                <p>{materialesPermitidos.length > 0
                                    ? 'Prueba con otro nombre o selecciona un nivel diferente.'
                                    : 'Aún no hay materiales disponibles para tu nivel.'}</p>
                            </div>
                        )}
                    </section>
                ) : (
                    <section className="material-library__empty" role="status">
                        <span className="material-library__empty-icon"><FiPrinter aria-hidden="true" /></span>
                        <h2>Tu material estará disponible pronto</h2>
                        <p>Aún no tienes un nivel asignado. Contacta a tu profesor para comenzar.</p>
                    </section>
                )}
            </main>
        </div>
    );
}

export { MaterialDidactico };
